import { useSyncExternalStore } from "react";
import { getRestaurantName } from "../../../../lib/restaurant";

interface LayoutStoreOptions<Layout> {
  /** localStorage ключът без ресторанта — `<prefix>:<ресторант>`. */
  storagePrefix: string;
  defaults: Layout;
  /** Каквото и да има в localStorage (ръчно пипнато, стара форма) — излиза
   *  размер в границите или по подразбиране. */
  normalize: (value: Partial<Record<keyof Layout, unknown>> | null) => Layout;
}

const sameLayout = <Layout extends object>(left: Layout, right: Layout) =>
  (Object.keys(right) as (keyof Layout)[]).every((key) => left[key] === right[key]);

/**
 * Хранилище за размерите на екран в „Маркиране" (масите, продуктите): пазят
 * се на КОМПЮТЪРА, като темата — localStorage["<prefix>:<ресторант>"] —
 * размерите зависят от екрана, не от човека. BE не ги вижда. С името на
 * ресторанта: на localhost всички ресторанти делят един localStorage.
 *
 * Влаченето на дръжка на кутийка е само предварителен изглед (`preview`) до
 * пускането (`commitPreview`): докато се влачи, кутийките НЕ се местят —
 * върху тях се рисуват сиви пунктирани контури на новия размер
 * (`CardLayoutPreview`); при пускане кутийките заемат точно тях.
 */
export const createLayoutStore = <Layout extends object>({
  storagePrefix,
  defaults,
  normalize,
}: LayoutStoreOptions<Layout>) => {
  const storageKey = () => `${storagePrefix}:${getRestaurantName()}`;

  const readStoredLayout = (): Layout => {
    try {
      const raw = localStorage.getItem(storageKey());
      return raw ? normalize(JSON.parse(raw)) : defaults;
    } catch {
      return defaults;
    }
  };

  let currentLayout: Layout | null = null;
  let currentPreview: Layout | null = null;
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((listener) => listener());

  const getLayout = (): Layout => {
    currentLayout ??= readStoredLayout();
    return currentLayout;
  };

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  /**
   * Сменя размерите. По време на влаченето (релсата) — само на екрана
   * (`persist: false`, за да не пише в localStorage на всяко мръдване); при
   * пускане — и на компютъра.
   */
  const update = (patch: Partial<Layout>, { persist }: { persist: boolean }): void => {
    currentLayout = { ...getLayout(), ...patch };
    notify();
    if (!persist) return;
    try {
      localStorage.setItem(storageKey(), JSON.stringify(currentLayout));
    } catch {
      // Без localStorage (частен прозорец) размерът важи до презареждане.
    }
  };

  /** Една стойност от размерите — компонентът се прерисува само когато тя се
   *  смени (влаченето на релсата не прерисува картите и обратно). */
  const useLayout = <Value>(select: (layout: Layout) => Value): Value =>
    useSyncExternalStore(subscribe, () => select(getLayout()));

  const preview = (patch: Partial<Layout>): void => {
    const next = { ...getLayout(), ...patch };
    // Мишката мърда постоянно, а размерът се сменя на стъпки (цяла колона,
    // пиксел височина) — прерисуване само при нов размер.
    if (currentPreview && sameLayout(currentPreview, next)) return;
    currentPreview = next;
    notify();
  };

  /** При пускане: предварителният изглед става размерът (и на компютъра). */
  const commitPreview = (): void => {
    const pending = currentPreview;
    if (!pending) return;
    currentPreview = null;
    update(pending, { persist: true });
  };

  /** Размерът, който ще стане при пускане (null — не се влачи кутийка). */
  const usePreview = <Value>(select: (pending: Layout | null) => Value): Value =>
    useSyncExternalStore(subscribe, () => select(currentPreview));

  return { update, useLayout, preview, commitPreview, usePreview };
};
